import { useMemo, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  groupPortsPaged,
  resolveRoute,
  type PortGroup,
  type RouterPort,
} from "./router-model";
import {
  PortMenu,
  RenameInput,
  portTitle,
  useRename,
  useStartRenameOnDoubleClick,
} from "./rename";
import { ROUTE_STATE_DOT, ROUTE_STATE_FILL } from "./route-style";
import type { RouterViewProps } from "./types";

/* ─────────────────────────────────────────────────────────── */
/* Button View — target → source button picker                  */
/* ─────────────────────────────────────────────────────────── */

const BUTTONS_PER_GROUP = 10;

export const RouterButtons = ({
  sources,
  targets,
  routes,
  routeState,
  routeFallbackLabel,
  sourcesById,
  allowClear,
  sourceNoun,
  targetNoun,
  canRouteTarget,
  clickSource,
  clearRoute,
}: RouterViewProps) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const targetGroups = useMemo(
    () => groupPortsPaged(targets, BUTTONS_PER_GROUP),
    [targets],
  );
  const sourceGroups = useMemo(
    () => groupPortsPaged(sources, BUTTONS_PER_GROUP),
    [sources],
  );

  const selected = targets.find((t) => t.id === selectedId) ?? null;
  const selectedRoute = selected
    ? resolveRoute(
        selected.id,
        routes,
        sourcesById,
        routeState,
        routeFallbackLabel,
      )
    : null;
  const canRouteSelected = selected ? canRouteTarget(selected) : false;

  return (
    <div className="space-y-4">
      {/* Targets */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
            {targetNoun}s
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ButtonGroups
            groups={targetGroups}
            renderPort={(target) => {
              const resolved = resolveRoute(
                target.id,
                routes,
                sourcesById,
                routeState,
                routeFallbackLabel,
              );
              return (
                <TargetButton
                  key={target.id}
                  target={target}
                  routeLabel={resolved.label}
                  routeDot={
                    resolved.state ? ROUTE_STATE_DOT[resolved.state] : null
                  }
                  unresolved={!resolved.source && !!resolved.label}
                  isSelected={selectedId === target.id}
                  onClick={() => setSelectedId(target.id)}
                />
              );
            }}
          />
        </CardContent>
      </Card>

      {/* Sources — shown when a target is selected */}
      {selected && selectedRoute && (
        <Card className="border-primary/50">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="truncate text-sm font-medium uppercase tracking-wide text-muted-foreground">
                {sourceNoun}s → {selected.label}
              </CardTitle>
              <div className="flex shrink-0 gap-1">
                {allowClear && selectedRoute.sourceId !== null && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={() => clearRoute(selected.id)}
                    disabled={!canRouteSelected}
                    data-testid="router-clear"
                  >
                    Clear
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs"
                  onClick={() => setSelectedId(null)}
                >
                  Close
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ButtonGroups
              key={selected.id}
              groups={sourceGroups}
              initialKey={
                sourceGroups.find((g) =>
                  g.ports.some((p) => p.id === selectedRoute.sourceId),
                )?.key
              }
              renderPort={(source) => (
                <SourceButton
                  key={source.id}
                  source={source}
                  isActive={selectedRoute.sourceId === source.id}
                  activeClass={
                    selectedRoute.state && selectedRoute.state !== "ok"
                      ? ROUTE_STATE_FILL[selectedRoute.state]
                      : "bg-green-600 text-white border-green-600"
                  }
                  onClick={() => {
                    // Re-clicking the routed source is a no-op here (use
                    // "Clear"), so a stray double-click never unroutes.
                    if (source.id !== selectedRoute.sourceId) {
                      clickSource(selected.id, source.id);
                    }
                  }}
                  disabled={!canRouteSelected || !!source.disabled}
                />
              )}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* Grouped grid with one expanded group at a time               */
/* ─────────────────────────────────────────────────────────── */

const ButtonGroups = ({
  groups,
  initialKey,
  renderPort,
}: {
  groups: PortGroup[];
  initialKey?: string;
  renderPort: (port: RouterPort) => ReactNode;
}) => {
  const [expandedKey, setExpandedKey] = useState<string | undefined>(
    initialKey,
  );
  const grid = "grid grid-cols-2 gap-1.5 sm:grid-cols-5";

  if (groups.length <= 1) {
    return <div className={grid}>{groups[0]?.ports.map(renderPort)}</div>;
  }

  // Fall back to the first group if the remembered one disappeared
  const effectiveKey = groups.some((g) => g.key === expandedKey)
    ? expandedKey
    : groups[0].key;

  return (
    <div className="space-y-3">
      {groups.map((group) => (
        <div key={group.key}>
          <button
            type="button"
            className="mb-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
            onClick={() => setExpandedKey(group.key)}
          >
            {group.label || "—"}
            {effectiveKey === group.key ? " ▾" : " ▸"}
          </button>
          {effectiveKey === group.key && (
            <div className={grid}>{group.ports.map(renderPort)}</div>
          )}
        </div>
      ))}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* Target Button                                                */
/* ─────────────────────────────────────────────────────────── */

interface TargetButtonProps {
  target: RouterPort;
  routeLabel: string;
  routeDot: string | null;
  unresolved: boolean;
  isSelected: boolean;
  onClick: () => void;
}

const TargetButton = ({
  target,
  routeLabel,
  routeDot,
  unresolved,
  isSelected,
  onClick,
}: TargetButtonProps) => {
  const rename = useRename();
  const onDoubleClick = useStartRenameOnDoubleClick("target", target);

  if (rename.isEditing("target", target.id)) {
    return (
      <div className="flex min-h-[52px] items-center rounded-md border border-primary p-1.5">
        <RenameInput kind="target" port={target} className="w-full" />
      </div>
    );
  }

  return (
    <PortMenu kind="target" port={target}>
      <button
        type="button"
        data-testid={`router-target-${target.id}`}
        aria-pressed={isSelected}
        title={portTitle(target)}
        className={cn(
          "flex min-h-[52px] flex-col items-start rounded-md border p-2 text-left transition-colors",
          isSelected
            ? "border-primary bg-primary/15 ring-2 ring-primary"
            : "border-border bg-card hover:bg-accent",
          target.disabled && "opacity-50",
        )}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
      >
        <span className="w-full truncate text-[11px] font-medium">
          {target.label}
        </span>
        <span
          className={cn(
            "flex w-full items-center gap-1 text-[10px]",
            "text-muted-foreground",
          )}
        >
          {routeDot && (
            <span
              className={cn("h-1.5 w-1.5 shrink-0 rounded-full", routeDot)}
            />
          )}
          <span className={cn("truncate", unresolved && "italic")}>
            ← {routeLabel || "None"}
          </span>
        </span>
      </button>
    </PortMenu>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* Source Button                                                */
/* ─────────────────────────────────────────────────────────── */

interface SourceButtonProps {
  source: RouterPort;
  isActive: boolean;
  activeClass: string;
  onClick: () => void;
  disabled?: boolean;
}

const SourceButton = ({
  source,
  isActive,
  activeClass,
  onClick,
  disabled,
}: SourceButtonProps) => {
  const rename = useRename();

  if (rename.isEditing("source", source.id)) {
    return (
      <div className="flex min-h-[44px] items-center rounded-md border border-primary p-1.5">
        <RenameInput kind="source" port={source} className="w-full" />
      </div>
    );
  }

  return (
    <PortMenu kind="source" port={source}>
      <button
        type="button"
        data-testid={`router-source-${source.id}`}
        aria-pressed={isActive}
        title={portTitle(source)}
        className={cn(
          "flex min-h-[44px] items-center justify-center rounded-md border p-2 text-center transition-colors",
          isActive ? activeClass : "border-border bg-card hover:bg-accent",
          disabled && "cursor-not-allowed opacity-50",
        )}
        // Clicking routes, so renaming a source is context-menu only here.
        // aria-disabled (not disabled) keeps the context menu reachable.
        aria-disabled={disabled}
        onClick={disabled ? undefined : onClick}
      >
        <span className="truncate text-[11px] font-medium">{source.label}</span>
      </button>
    </PortMenu>
  );
};
